import { ThemeTypeDropdown } from '@/components/dropdowns/ThemeTypeDropdown';
import type { Tour } from '@/config/objects/tours';
import { Button, useApiClient } from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Edit2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@startupquickstart/frontend';

interface TourThemeInlineEditProps {
  tour?: Tour;
}

/**
 * Inline editable component for tour's default theme type
 * Click to edit, shows dropdown, auto-saves on change
 */
export function TourThemeInlineEdit({ tour }: TourThemeInlineEditProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedThemeId, setSelectedThemeId] = useState<string | undefined>(
    tour?.defaultThemeTypeId || undefined
  );
  const [isUpdating, setIsUpdating] = useState(false);

  if (!tour) return null;

  const handleEdit = () => {
    setIsEditing(true);
    setSelectedThemeId(tour.defaultThemeTypeId || undefined);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedThemeId(tour.defaultThemeTypeId || undefined);
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      await apiClient.patch(`/tours/${tour.id}`, {
        defaultThemeTypeId: selectedThemeId || null,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['Tour'] });

      toast.success('Tour theme updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating tour theme:', error);
      toast.error('Failed to update tour theme');
    } finally {
      setIsUpdating(false);
    }
  };

  // Not editing - show current value with edit button
  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className={!tour.defaultThemeType ? 'text-muted-foreground' : ''}>
          {tour.defaultThemeType?.name || 'No theme set'}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleEdit}
          className="h-7 w-7 p-0"
        >
          <Edit2 className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  // Editing - show dropdown with save/cancel buttons
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <ThemeTypeDropdown
          value={selectedThemeId}
          onChange={setSelectedThemeId}
          placeholder="Select theme type"
        />
      </div>
      <div className="flex gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleSave}
          disabled={isUpdating}
          className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isUpdating}
          className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
